import { useEffect, useState } from "react";
import { Mail, Loader2, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { getReportSettings, setReportSettings, sendReportNow } from "@/lib/api";

/** Weekly email report: owner enters their email, toggles on, or sends now. */
export default function WeeklyReportCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { getReportSettings().then(setS).catch(() => {}); }, []);
  if (!s) return null;

  const save = async (patch) => {
    const next = { enabled: s.enabled, recipient: s.recipient, ...patch };
    setSaving(true);
    try { const d = await setReportSettings(next); setS(d); toast.success("Report settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try { const d = await sendReportNow(); toast.success(`Report sent to ${d.sent_to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed — set a recipient first"); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 card-shadow p-5 mb-6" data-testid="weekly-report-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand/10 text-brand"><Mail size={16} /></span>
        <div>
          <div className="font-heading font-bold text-slate-900 leading-tight">Weekly email report</div>
          <div className="text-[11px] text-slate-400">A plain-English summary of your real numbers, emailed every week</div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <input type="email" value={s.recipient || ""} onChange={(e) => setS({ ...s, recipient: e.target.value })}
          placeholder="you@yourbusiness.com" data-testid="report-recipient"
          className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none" />
        <label className="flex items-center gap-2 text-sm text-slate-600 shrink-0">
          <input type="checkbox" checked={s.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="report-enabled" />
          Weekly
        </label>
        <button onClick={() => save({})} disabled={saving} data-testid="report-save"
          className="inline-flex items-center gap-1.5 btn-outline rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
        </button>
        <button onClick={sendNow} disabled={sending} data-testid="report-send-now"
          className="inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send now
        </button>
      </div>
      {s.last_sent_at && <div className="text-[11px] text-slate-400 mt-2">Last sent: {s.last_sent_at.slice(0, 16).replace("T", " ")}</div>}
    </div>
  );
}
